import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Text, Animated, Platform } from 'react-native';
import { AVPlaybackStatus } from 'expo-av';
import PlayIcon from './icons/PlayIcon';
import PauseIcon from './icons/PauseIcon';
import ReplayIcon from './icons/ReplayIcon';
import SeekBar from './common/SeekBar';
import { formatTime } from '../utils/formatTime';
import { scaleFont } from '../utils/typography';

type VideoControlsProps = {
  visible: boolean;
  status: AVPlaybackStatus;
  onPlayPause: () => void;
  onSeek: (position: number) => void;
  onReplay: () => void;
  onInteraction: () => void;
};

const VideoControls: React.FC<VideoControlsProps> = ({
  visible,
  status,
  onPlayPause,
  onSeek,
  onReplay,
  onInteraction,
}) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const [isSeeking, setIsSeeking] = useState(false);

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: visible ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, [visible]);

  if (!status.isLoaded) {
    return null;
  }

  const currentPosition = status.positionMillis || 0;
  const totalDuration = status.durationMillis || 0;
  const isPlaying = status.isPlaying;

  const handleSeekStart = () => {
    setIsSeeking(true);
    onInteraction();
  };

  const handleSeek = (position: number) => {
    onSeek(position);
    setIsSeeking(false);
  };

  const handleSeekEnd = () => {
    onInteraction();
  };

  const handlePlayPause = () => {
    onPlayPause();
    onInteraction();
  };

  const handleReplay = () => {
    onReplay();
    onInteraction();
  };

  return (
    <Animated.View
      style={[
        styles.container,
        Platform.OS === 'web' && styles.containerWeb,
        {
          opacity: fadeAnim,
        },
      ]}
      pointerEvents={visible ? 'auto' : 'none'}
    >
      <View style={styles.controlsRow}>
        <TouchableOpacity
          style={styles.button}
          onPress={handlePlayPause}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          {isPlaying ? (
            <PauseIcon size={24} color="#ffffff" />
          ) : (
            <PlayIcon size={24} color="#ffffff" />
          )}
        </TouchableOpacity>

        <Text style={styles.timeText}>
          {formatTime(isSeeking ? currentPosition : currentPosition)} / {formatTime(totalDuration)}
        </Text>

        <View style={styles.seekBarContainer}>
          <SeekBar
            position={currentPosition}
            duration={totalDuration}
            onSeek={handleSeek}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
          />
        </View>

        <TouchableOpacity
          style={styles.button}
          onPress={handleReplay}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <ReplayIcon size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 12,
    paddingVertical: 12,
    zIndex: 10,
  },
  containerWeb: {
    // @ts-ignore - web-specific styles
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  } as any,
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  button: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  timeText: {
    color: '#ffffff',
    fontSize: scaleFont(14),
    fontFamily: 'Urbanist_Medium',
    minWidth: 90,
  },
  seekBarContainer: {
    flex: 1,
    marginHorizontal: 8,
  },
});

export default VideoControls;
